import React from 'react';
import {format} from 'd3-format';
import {sum} from 'd3-array';
import { useHistory } from 'react-router-dom';
import { FarmContext } from './FarmContext';
import { AppContext } from './AppContext';
import ExtraEditor from './ExtraEditor';
import { GeoSVG, getArea } from './geo';
import Warning from './Warning';
import Attachments from './Attachments';
import {saveComp, deleteComp} from './db';
import { DEFAULT_PATH } from './App';
import { savedIcon } from './Notifications';


export const BoundaryPropEditor = ({f, updateProps}) => {
  return <div key={f.id} className="flex items-center mb-6">
     <div className="w-1/3">
       <label className="block text-gray-500 font-bold text-right mb-1 pr-4">
         { `${f.name || f.id} (${ format('.1f')(getArea(f.geometry)) } ac)` } 
       </label>
       <GeoSVG geometry={f.geometry} />
     </div>
     <div className="w-2/3">
      <ExtraEditor
        json={f.properties}
        onChangeJSON={updateProps}
      />
     </div>
  </div>
}


const toNumber = num => {
  // drf doesn't like '' for numbers
  if (num === '') return null;
  return num;
}


export const Input = ({ id, type, prefix, suffix, placeholder }) => {
  const { formData, setFormData } = React.useContext(FarmContext);
  const value = formData[id];
  const onChange = v => setFormData(d => ({...d, [id]: v}));

  if (type === 'Inline') {
    return <div className="flex rounded">
      {prefix && <span className="inline-flex items-center px-2 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
       {prefix}
      </span>}
      <input
        className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-36 block py-1 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-purple-500"
        type="number" value={value || ''} onChange={e => onChange(toNumber(e.target.value))}
        placeholder={placeholder}
      />
      {suffix && <span className="inline-flex items-center px-2 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
       {suffix}
      </span>}
    </div>
  }
  if (type === 'Number') {
    return <input
       className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-1/2 py-1 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-purple-500"
       type="number" value={value || ''} onChange={e => onChange(toNumber(e.target.value))}
       placeholder={placeholder}
     />
  }
  if (type === 'Text') {
    return <textarea
      className="w-full h-24 bg-gray-200 appearance-none border-2 border-gray-200 rounded w-1/2 py-1 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-purple-500"
      value={value || ''} onChange={e => onChange(e.target.value)}
    />
  }
  if (type === 'Date') {
    return <input
       className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-1/2 py-1 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-purple-500"
       type="date" value={value || ''} onChange={e => onChange(e.target.value)}
     />
  }
  if (type === 'Object') {
    return <ExtraEditor
      json={value || {}}
      onChangeJSON={onChange}
    />
  }
  if (type === 'CompType') {
    return <select
      id="comptype"
      name="comptype"
      className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-1/2 py-1 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-purple-500"
      value={value || "sale"}
      onChange={e => onChange(e.target.value)}
    >
      <option value="sale">Sale</option>
      <option value="listing">Listing</option>
      <option value="rejected">Rejected</option>
    </select>
  }
  return <input
     className="bg-gray-200 appearance-none border-2 border-gray-200 rounded w-1/2 py-1 px-4 text-gray-700 leading-tight focus:outline-none focus:bg-white focus:border-purple-500"
     type="text" value={value || ''} onChange={e => onChange(e.target.value)}
   />
}

export const Label = ({l}) => {
  return <div className="w-1/3">
     <label className="block text-gray-500 font-bold text-right mb-1 pr-4">
       {l}
     </label>
   </div>
}


export const GenericField = ({k, children}) => {
  return <div className="space-y-6">
    <div className="">
      <label className="block text-sm font-medium text-gray-700">
        {k.label ||  k.id}
      </label>
      <div className="mt-1">
        {children}
      </div>
    </div>
  </div>

  // responsive
  // return <div className="space-y-6 sm:space-y-5">
  //   <div className="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5">
  //     <label className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
  //       {k.label ||  k.id}
  //     </label>
  //     <div className="mt-1 sm:mt-0 sm:col-span-2">
  //       {children}
  //     </div>
  //   </div>
  // </div>
}


export const FormField = ({k}) => {
  return <GenericField k={k}>
    <Input
      id={k.id}
      type={k.type}
      placeholder={k.placeholder}
    />
  </GenericField>
}



export const getAcres = (boundary, formData) => {
  const total_acres_est = sum(boundary.features, f => getArea(f.geometry));
  const acres = formData.total_acres || total_acres_est;
  return acres;
}


// used by ClusterMap, but specificied here because should be consistant with this
// note this "List View" serializer computes acres on backend in sql query
export const getPricePerAcre = comp => {
  if (comp.price_per_acre) return comp.price_per_acre;
  const total_acres_est = sum(comp['boundary_centroid_set'], b => b['properties']['acres']);
  const acres = comp.total_acres || total_acres_est;
  if (comp.price) return Math.round(comp['price'] / acres);
}


export default function Data() {
  const { compId, boundary, setBoundary, formData } = React.useContext(FarmContext);
  const { user, notify } = React.useContext(AppContext);
  const history = useHistory();

  // TODO soil legend with muname (musym) + CORN:
  //  interphr
  //  nonirryield_r
  //  irryield_r

  // TODO
  // Total Acres
  // Tillable Acres
  // Total Price OR Price per acre
  // Any Notes to add

  const saveToServer = React.useCallback(() => {
    
    notify('Comp Successfully Saved', 'You can share it using the url.', savedIcon);
    saveComp(user, compId, formData, boundary)
      .then(f => {
        console.log(f)
        history.push('/comp/' + f.id);
      });

    // parcel_id
    // boundary
    // tillable_acres
    // price
    // rent
    // date_sold
  }, [user, compId, boundary, formData, history, notify]);


  const deleteButton = React.useCallback(() => {
    const del = window.confirm('Are you sure you want to delete this comp?')
    if (del) {
      notify('Comp Successfully Deleted', '', savedIcon);
      deleteComp(user, compId).then(f => {
          history.push(DEFAULT_PATH);
        });
    }
  }, [user, compId, history, notify])

  if (boundary.features.length === 0) return <Warning text="No farm boundary, go and draw one on the farm map." />

  const credit = (compId === 'new') ? 'Create' : 'Edit';

  const lupdate = formData.source_user ? `Last update: ${formData.source_user} on ${formData.updated_at && formData.updated_at.slice(0, 10)}` : '';

  const acres = getAcres(boundary, formData);

  return <div className="w-full" >
    <div className="pt-4 space-y-6 sm:pt-10 sm:space-y-5">
      <div>
        <h3 className="text-lg leading-6 font-medium text-gray-900">Comp Properties</h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">{credit} a Comp entry. {lupdate}</p>
      </div>

      <GenericField k={{id: "Price"}}>
          <Input
            prefix={'$'}
            id={'price'}
            type={'Inline'}
            placeholder={formData.price_per_acre && format('.0f')(formData.price_per_acre * acres)}
          />
          <span> OR </span>
          <Input
            prefix={'$'}
            id={'price_per_acre'}
            suffix={' / acre'}
            type={'Inline'}
            placeholder={formData.price && format('.0f')(formData.price / acres)}
          />
      </GenericField>
      {/*
      <span> Boundary is {format('.1f')(sum(boundary.features, f => getArea(f.geometry)))} acres</span>
      */}

 
      <FormField k={{label: 'Total Acres', 'id': 'total_acres', 'type': 'Number', placeholder: format('.1f')(acres)}} />
      <FormField k={{label: 'Tillable Acres',  'id': 'tillable_acres', 'type': 'Number'}} />
      <FormField k={{label: 'Date Sold', 'id': 'date_sold', 'type': 'Date'}} />
      <FormField k={{label: 'Rent', 'id': 'rent', 'type': 'Number'}} />
      <FormField k={{label: 'Sale / Listing / Rejected', 'id': 'comp_type', 'type': 'CompType'}} />
      <FormField k={{label: 'Notes', 'id': 'notes', 'type': 'Text'}} />
      
      <GenericField k={{id: 'Attachments'}} >
         <Attachments model='comp' />
      </GenericField>

      <FormField k={{'id': 'extra', 'type': 'Object'}} />

      <h3 className="text-lg leading-6 font-medium text-gray-900">Boundary Properties</h3>

      {boundary.features.map(f => {
        return <BoundaryPropEditor key={f.id} f={f} updateProps={newProps => {
          setBoundary(fc => ({...fc, features: fc.features.map(fOld => {
            if (fOld.id === f.id) return {...fOld, properties: newProps};
            return fOld;
          })}))
        }} />
      })}


      <div className="pt-5">
        <div className="flex justify-end">
          {(compId !== 'new') && <button
            className="shadow bg-red-500 hover:bg-red-400 focus:shadow-outline focus:outline-none text-white font-bold py-2 px-4 rounded"
            type="button"
            onClick={deleteButton}
          >
            Delete
          </button>}

          <button
            className="ml-4 shadow bg-purple-500 hover:bg-purple-400 focus:shadow-outline focus:outline-none text-white font-bold py-2 px-4 rounded"
            type="button"
            onClick={saveToServer}
          >
            Save
          </button>
        </div>
      </div>



    </div>
  </div>

}
